import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../../components/Seo';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../auth/AuthProvider';

const PAGE_SIZE = 12;
const VIEW_MODES = ['list', 'feed', 'card', 'grid'];

const htmlToText = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return String(doc?.body?.textContent || '').trim();
  } catch {
    return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
};

const FeedPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, Number(searchParams.get('page') || '1'));
  const rawView = String(searchParams.get('view') || 'list');
  const viewMode = VIEW_MODES.includes(rawView) ? rawView : 'list';

  const [items, setItems] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const offset = useMemo(() => (page - 1) * PAGE_SIZE, [page]);

  const updateQuery = useCallback(
    (next) => {
      const q = new URLSearchParams(searchParams);
      Object.entries(next || {}).forEach(([k, v]) => {
        if (v == null || v === '') q.delete(k);
        else q.set(k, String(v));
      });
      setSearchParams(q);
    },
    [searchParams, setSearchParams]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const from = offset;
      const to = offset + PAGE_SIZE;

      const { data: rows, error: postErr } = await supabase
        .from('ch_feed_posts')
        .select('id, title, author_id, author_name, content_html, created_at, is_published, thumbnail_image_path, view_count')
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (postErr) {
        setItems([]);
        setHasNext(false);
        setError(postErr.message || '피드 목록을 불러오지 못했습니다.');
        return;
      }

      const pageRows = rows || [];
      setHasNext(pageRows.length > PAGE_SIZE);
      const sliced = pageRows.slice(0, PAGE_SIZE);

      const ids = sliced.map((r) => r.id).filter(Boolean);
      let imageMap = new Map();
      if (ids.length > 0) {
        const [{ data: imageRows }, feedLikeRowsRes, feedCommentRowsRes] = await Promise.all([
          supabase
            .from('ch_feed_images')
            .select('post_id, file_path, created_at')
            .in('post_id', ids)
            .order('created_at', { ascending: true }),
          supabase
            .from('ch_feed_post_likes')
            .select('post_id')
            .in('post_id', ids),
          supabase
            .from('ch_feed_comments')
            .select('post_id')
            .in('post_id', ids),
        ]);

        imageMap = (imageRows || []).reduce((acc, row) => {
          if (!row?.post_id || acc.has(row.post_id)) return acc;
          acc.set(row.post_id, row.file_path || '');
          return acc;
        }, new Map());

        const likeCountByPostId = (feedLikeRowsRes?.data || []).reduce((acc, row) => {
          if (!row?.post_id) return acc;
          acc.set(row.post_id, (acc.get(row.post_id) || 0) + 1);
          return acc;
        }, new Map());

        const commentCountByPostId = (feedCommentRowsRes?.data || []).reduce((acc, row) => {
          if (!row?.post_id) return acc;
          acc.set(row.post_id, (acc.get(row.post_id) || 0) + 1);
          return acc;
        }, new Map());

        setItems(
          sliced.map((row) => {
            const thumbnailPath = row.thumbnail_image_path || imageMap.get(row.id) || '';
            const thumbnailUrl = thumbnailPath
              ? supabase.storage.from('feed-images').getPublicUrl(thumbnailPath).data.publicUrl
              : '';

            return {
              id: row.id,
              title: row.title || '제목 없음',
              authorId: row.author_id,
              authorName: row.author_name || '익명',
              createdAt: row.created_at ? String(row.created_at).slice(0, 10) : '',
              excerpt: htmlToText(row.content_html).slice(0, 160),
              thumbnailUrl,
              likeCount: Number(likeCountByPostId.get(row.id) || 0),
              commentCount: Number(commentCountByPostId.get(row.id) || 0),
              viewCount: Number(row.view_count || 0),
            };
          })
        );
        return;
      }

      setItems(
        sliced.map((row) => ({
          id: row.id,
          title: row.title || '제목 없음',
          authorId: row.author_id,
          authorName: row.author_name || '익명',
          createdAt: row.created_at ? String(row.created_at).slice(0, 10) : '',
          excerpt: htmlToText(row.content_html).slice(0, 160),
          thumbnailUrl: '',
          likeCount: 0,
          commentCount: 0,
          viewCount: Number(row.view_count || 0),
        }))
      );
    } finally {
      setIsLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    load();
  }, [load]);

  const cardClassName = useMemo(() => {
    if (viewMode === 'grid') return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4';
    if (viewMode === 'card') return 'grid grid-cols-1 md:grid-cols-2 gap-4';
    return 'space-y-4';
  }, [viewMode]);

  return (
    <div className="min-h-screen bg-background pt-16">
      <Seo title="피드" canonicalPath="/feed" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-5 lg:px-8 py-5 md:py-8 lg:py-12">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">피드</h1>
            <p className="text-sm text-muted-foreground mt-1">게시판 형태로 다양한 보기 모드를 지원합니다.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {VIEW_MODES.map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => updateQuery({ view: mode, page: 1 })}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-gaming ${
                  viewMode === mode
                    ? 'bg-primary text-primary-foreground shadow-gaming-sm'
                    : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {mode === 'list' ? '리스트형' : mode === 'feed' ? '피드형' : mode === 'card' ? '카드형' : '그리드형'}
              </button>
            ))}
            <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => navigate('/feed-editor')}>
              글 등록
            </Button>
          </div>
        </div>

        {error ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border mb-4">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        {isLoading ? (
          <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
            <p className="text-sm text-muted-foreground caption">로딩 중...</p>
          </div>
        ) : (
          <div className={cardClassName}>
            {(items || []).length === 0 ? (
              <div className="bg-card rounded-lg shadow-gaming-md p-6 border border-border">
                <p className="text-sm text-muted-foreground caption">아직 등록된 글이 없습니다.</p>
              </div>
            ) : (
              (items || []).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigate(`/feed-detail?id=${item.id}`)}
                  className="w-full text-left bg-card rounded-lg border border-border overflow-hidden hover:shadow-gaming-md transition-gaming"
                >
                  {viewMode === 'list' ? (
                    <div className="p-4 flex items-start gap-4">
                      <div className="w-28 sm:w-36 h-20 sm:h-24 rounded-md bg-muted border border-border overflow-hidden flex-shrink-0">
                        {item.thumbnailUrl ? (
                          <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[11px] text-muted-foreground caption">
                            이미지 없음
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-lg font-heading font-semibold text-foreground line-clamp-2">{item.title}</div>
                        <div className="text-xs text-muted-foreground caption mt-1">
                          {item.createdAt} · {item.authorName}
                        </div>
                        <div className="text-xs text-muted-foreground caption mt-1">
                          좋아요 {Number(item.likeCount || 0)} · 댓글 {Number(item.commentCount || 0)} · 조회 {Number(item.viewCount || 0)}
                        </div>
                        <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{item.excerpt || '내용 없음'}</p>
                      </div>
                    </div>
                  ) : null}

                  {viewMode !== 'list' && item.thumbnailUrl ? (
                    <div className={`${viewMode === 'feed' ? 'h-56' : 'h-44'} bg-muted border-b border-border`}>
                      <img src={item.thumbnailUrl} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                  ) : null}

                  {viewMode !== 'list' ? (
                    <div className="p-4">
                      <div className="text-lg font-heading font-semibold text-foreground line-clamp-2">{item.title}</div>
                      <div className="text-xs text-muted-foreground caption mt-1">
                        {item.createdAt} · {item.authorName}
                      </div>
                      <div className="text-xs text-muted-foreground caption mt-1">
                        좋아요 {Number(item.likeCount || 0)} · 댓글 {Number(item.commentCount || 0)} · 조회 {Number(item.viewCount || 0)}
                      </div>
                      {viewMode === 'feed' ? (
                        <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{item.excerpt || '내용 없음'}</p>
                      ) : null}
                    </div>
                  ) : null}
                </button>
              ))
            )}
          </div>
        )}

        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={() => updateQuery({ page: Math.max(1, page - 1) })}
            disabled={page <= 1 || isLoading}
            className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
          >
            이전
          </button>

          <div className="text-sm text-muted-foreground caption">페이지 {page}</div>

          <button
            type="button"
            onClick={() => updateQuery({ page: page + 1 })}
            disabled={!hasNext || isLoading}
            className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
          >
            다음
          </button>
        </div>

        {!user ? (
          <div className="mt-6 text-xs text-muted-foreground caption">글 등록/수정/삭제는 로그인 후 이용할 수 있습니다.</div>
        ) : null}
      </main>
    </div>
  );
};

export default FeedPage;
